// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FC } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Card } from '@mui/material';
import { Input, Label, Translation } from '@shared/ui-kit';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { useBuyOperationData } from '@shared/portfolio-operations/providers';
import type { BuyOperationFormData } from '@shared/portfolio-operations/schemas/buy-operation';
import { CurrentPositionDisplay } from '../current-position-display/current-position-display';

export const BuyOperationFormContent: FC = () => {
  const { sourceAsset, form, currencyPrefix, onBlur, amountOnly } =
    useBuyOperationData();

  return (
    <FormProvider {...form}>
      <div className="space-y-3">
        <CurrentPositionDisplay
          investment={sourceAsset.investment}
          quantity={sourceAsset.quantity}
          currency={sourceAsset.currency}
        />

        <Card
          variant="outlined"
          className="!rounded-lg !bg-white !p-3 transition-colors hover:!bg-gray-50"
        >
          <div className="flex items-start gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Label className="text-base font-semibold">
                <Translation ns="components" i18nKey="ticker-modal.amount-label" />
              </Label>
              <Controller<BuyOperationFormData, 'amount'>
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <NumericFormat
                    customInput={Input}
                    value={field.value}
                    name="amount"
                    onValueChange={({ floatValue }) =>
                      field.onChange(floatValue ?? 0)
                    }
                    onBlur={e => {
                      field.onBlur();
                      onBlur(e);
                    }}
                    prefix={currencyPrefix}
                    allowNegative={false}
                    decimalScale={0}
                    fixedDecimalScale={false}
                    allowedDecimalSeparators={DECIMAL_SEPARATORS}
                    thousandSeparator
                    className="w-full"
                    data-testid="amount-to-invest-input"
                    autoFocus
                  />
                )}
              />
            </div>
            {!amountOnly && (
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Label className="text-base font-semibold">
                  <Translation
                    ns="components"
                    i18nKey="ticker-modal.shares-label"
                  />
                </Label>
                <Controller<BuyOperationFormData, 'shares'>
                  control={form.control}
                  name="shares"
                  render={({ field }) => (
                    <NumericFormat
                      customInput={Input}
                      value={field.value}
                      name="shares"
                      onValueChange={({ floatValue }) =>
                        field.onChange(floatValue ?? 0)
                      }
                      onBlur={e => {
                        field.onBlur();
                        onBlur(e);
                      }}
                      allowNegative={false}
                      decimalScale={2}
                      fixedDecimalScale={false}
                      allowedDecimalSeparators={DECIMAL_SEPARATORS}
                      thousandSeparator
                      className="w-full"
                      data-testid="shares-to-buy-input"
                    />
                  )}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    </FormProvider>
  );
};
